/**
 * ConnectFourBoard React Component
 *
 * Renders an interactive 7×6 Connect Four grid with circular cell slots,
 * Red/Yellow discs, column hover preview, disc drop animation, winning cell
 * highlight, last move highlight, AI thinking indicator, keyboard navigation,
 * and ARIA labels.
 *
 * Requirements: 1.1, 1.3, 1.4, 2.4, 7.2, 7.5, 16.1–16.4, 19.1, 19.3,
 *               19.4, 21.3, 21.5, 33.1–33.5, 35.4, 35.5, 40.1–40.6
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ConnectFourEngine } from '../ConnectFourEngine';
import {
  Position,
  ConnectFourMove,
  ConnectFourGameResult,
  DiscColor,
  ROWS,
  COLS,
  positionsEqual,
} from '../types';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import './ConnectFourBoard.css';

export interface ConnectFourBoardProps {
  engine: ConnectFourEngine;
  interactive?: boolean;
  isAIThinking?: boolean;
  onMove?: (move: ConnectFourMove) => void;
  onGameEnd?: (result: ConnectFourGameResult) => void;
  moveCount?: number;
  playerColor?: DiscColor;
}

/**
 * ConnectFourBoard renders the 7×6 grid and handles user interactions.
 */
export const ConnectFourBoard: React.FC<ConnectFourBoardProps> = ({
  engine,
  interactive = true,
  isAIThinking = false,
  onMove,
  onGameEnd,
  moveCount,
  playerColor,
}) => {
  const [hoveredColumn, setHoveredColumn] = useState<number | null>(null);
  const [droppingCell, setDroppingCell] = useState<{ row: number; col: number } | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [focusedColumn, setFocusedColumn] = useState(0);

  const boardRef = useRef<HTMLDivElement>(null);

  const gameState = engine.getGameState();
  const grid = gameState.grid;
  const currentPlayer = gameState.currentPlayer;
  const gameStatus = gameState.gameStatus;
  const winningCells = gameState.winningCells;
  const moveHistory = gameState.moveHistory;
  const lastMove = moveHistory.length > 0 ? moveHistory[moveHistory.length - 1] : null;

  const canInteract = interactive && !isAIThinking && gameStatus.type === 'active';

  // Check for game end
  useEffect(() => {
    if (gameStatus.type !== 'active' && onGameEnd) {
      if (gameStatus.type === 'win') {
        onGameEnd({
          gameId: '',
          mode: 'singleplayer',
          winner: gameStatus.winner,
          isDraw: false,
          reason: gameStatus.direction,
          moveCount: gameState.moveCount,
          duration: 0,
          completedAt: Date.now(),
        });
      } else if (gameStatus.type === 'draw') {
        onGameEnd({
          gameId: '',
          mode: 'singleplayer',
          isDraw: true,
          reason: 'draw',
          moveCount: gameState.moveCount,
          duration: 0,
          completedAt: Date.now(),
        });
      } else if (gameStatus.type === 'resignation') {
        onGameEnd({
          gameId: '',
          mode: 'singleplayer',
          winner: gameStatus.winner,
          isDraw: false,
          reason: 'resignation',
          moveCount: gameState.moveCount,
          duration: 0,
          completedAt: Date.now(),
        });
      }
    }
  }, [gameStatus, onGameEnd, gameState.moveCount]);

  /**
   * Handle column click to drop a disc.
   */
  const handleColumnClick = useCallback(
    (col: number) => {
      if (!canInteract) return;
      if (engine.isColumnFull(col)) return;

      const row = engine.getLowestEmptyRow(col);
      if (row === null) return;

      // Trigger drop animation
      setDroppingCell({ row, col });

      // Execute move after brief delay for animation start
      setTimeout(() => {
        const result = engine.dropDisc(col);

        if (result.success && result.move) {
          const colorName = result.move.player === 'red' ? 'Red' : 'Yellow';
          setAnnouncement(`${colorName} dropped disc in column ${col + 1}, row ${row + 1}`);

          if (result.gameStatus.type === 'win') {
            const winner = result.gameStatus.winner === 'red' ? 'Red' : 'Yellow';
            setAnnouncement(`${winner} wins!`);
          } else if (result.gameStatus.type === 'draw') {
            setAnnouncement('Game is a draw!');
          }

          if (onMove) {
            onMove(result.move);
          }

          // Haptic feedback
          if ('vibrate' in navigator) {
            navigator.vibrate(15);
          }
        }

        // Clear drop animation after it completes
        setTimeout(() => setDroppingCell(null), 400);
      }, 50);
    },
    [canInteract, engine, onMove]
  );

  /**
   * Handle keyboard navigation.
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!canInteract) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          setFocusedColumn((prev) => Math.max(0, prev - 1));
          break;
        case 'ArrowRight':
          e.preventDefault();
          setFocusedColumn((prev) => Math.min(COLS - 1, prev + 1));
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          handleColumnClick(focusedColumn);
          break;
      }
    },
    [canInteract, focusedColumn, handleColumnClick]
  );

  /**
   * Check if a cell is a winning cell.
   */
  const isWinningCell = (row: number, col: number): boolean => {
    if (!winningCells) return false;
    return winningCells.some((pos) => positionsEqual(pos, { row, col }));
  };

  /**
   * Check if a cell is the last move.
   */
  const isLastMoveCell = (row: number, col: number): boolean => {
    if (!lastMove) return false;
    return lastMove.row === row && lastMove.column === col;
  };

  /**
   * Get ARIA label for a cell.
   */
  const getCellAriaLabel = (row: number, col: number): string => {
    const cell = grid[row]?.[col];
    const colNum = col + 1;
    const rowNum = row + 1;

    if (cell === 'red') {
      return `Column ${colNum}, Row ${rowNum}, Red disc`;
    } else if (cell === 'yellow') {
      return `Column ${colNum}, Row ${rowNum}, Yellow disc`;
    }
    return `Column ${colNum}, Row ${rowNum}, empty`;
  };

  /**
   * Render the preview row at the top.
   */
  const renderPreviewRow = () => {
    return (
      <div className="cf-board__preview-row">
        {Array.from({ length: COLS }, (_, col) => {
          const showPreview =
            canInteract &&
            hoveredColumn === col &&
            !engine.isColumnFull(col);
          const isFocused = canInteract && focusedColumn === col;

          return (
            <div
              key={`preview-${col}`}
              className={`cf-board__preview-cell ${!canInteract ? 'cf-board__preview-cell--disabled' : ''}`}
              onMouseEnter={() => canInteract && setHoveredColumn(col)}
              onMouseLeave={() => setHoveredColumn(null)}
              onClick={() => handleColumnClick(col)}
              onTouchStart={(e) => {
                if (!canInteract) return;
                e.preventDefault();
                handleColumnClick(col);
              }}
              tabIndex={isFocused ? 0 : -1}
              role="button"
              aria-label={`Drop disc in column ${col + 1}`}
              onKeyDown={handleKeyDown}
              onFocus={() => setFocusedColumn(col)}
            >
              {(showPreview || (isFocused && !engine.isColumnFull(col))) && (
                <div
                  className={`cf-board__preview-disc cf-board__preview-disc--${currentPlayer}`}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  /**
   * Render the board grid (row 5 at top, row 0 at bottom).
   */
  const renderGrid = () => {
    // Display rows from top (5) to bottom (0)
    const displayRows = [];
    for (let displayRow = ROWS - 1; displayRow >= 0; displayRow--) {
      for (let col = 0; col < COLS; col++) {
        const row = displayRow;
        const cell = grid[row]?.[col] ?? null;
        const winning = isWinningCell(row, col);
        const isLast = isLastMoveCell(row, col);
        const isDropping =
          droppingCell && droppingCell.row === row && droppingCell.col === col;

        const cellClasses = [
          'cf-cell',
          winning ? 'cf-cell--winning' : '',
          isLast && !winning ? 'cf-cell--last-move' : '',
        ]
          .filter(Boolean)
          .join(' ');

        displayRows.push(
          <div
            key={`${row}-${col}`}
            className={cellClasses}
            onClick={() => handleColumnClick(col)}
            onMouseEnter={() => canInteract && setHoveredColumn(col)}
            onMouseLeave={() => setHoveredColumn(null)}
            role="gridcell"
            aria-label={getCellAriaLabel(row, col)}
            data-row={row}
            data-col={col}
          >
            <div className="cf-cell__slot">
              {cell && (
                <div
                  className={`cf-disc cf-disc--${cell} ${isDropping ? 'cf-disc--dropping' : ''}`}
                />
              )}
            </div>
          </div>
        );
      }
    }
    return displayRows;
  };

  /**
   * Render game over overlay.
   */
  const renderGameOverOverlay = () => {
    if (gameStatus.type === 'active') return null;

    if (gameStatus.type === 'win') {
      const winnerLabel = gameStatus.winner === 'red' ? 'Red' : 'Yellow';
      return (
        <div className="cf-board__game-over">
          <div className="cf-board__game-over-content">
            <div className="cf-board__game-over-icon">🏆</div>
            <div className="cf-board__game-over-title">{winnerLabel} Wins!</div>
            <div className="cf-board__game-over-message">
              {gameStatus.direction === 'horizontal' && 'Four in a row horizontally!'}
              {gameStatus.direction === 'vertical' && 'Four in a row vertically!'}
              {gameStatus.direction === 'diagonal-asc' && 'Four in a row diagonally!'}
              {gameStatus.direction === 'diagonal-desc' && 'Four in a row diagonally!'}
            </div>
          </div>
        </div>
      );
    }

    if (gameStatus.type === 'draw') {
      return (
        <div className="cf-board__game-over">
          <div className="cf-board__game-over-content">
            <div className="cf-board__game-over-icon">🤝</div>
            <div className="cf-board__game-over-title">Draw!</div>
            <div className="cf-board__game-over-message">The board is full. No winner.</div>
          </div>
        </div>
      );
    }

    if (gameStatus.type === 'resignation') {
      const winnerLabel = gameStatus.winner === 'red' ? 'Red' : 'Yellow';
      return (
        <div className="cf-board__game-over">
          <div className="cf-board__game-over-content">
            <div className="cf-board__game-over-icon">🏳️</div>
            <div className="cf-board__game-over-title">{winnerLabel} Wins!</div>
            <div className="cf-board__game-over-message">Opponent resigned</div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="cf-board-container">
      <div className="cf-board" ref={boardRef} role="grid" aria-label="Connect Four Board">
        {renderPreviewRow()}

        <div className="cf-board__grid">
          {renderGrid()}
        </div>

        {/* AI Thinking Indicator */}
        {isAIThinking && (
          <div className="cf-board__ai-thinking">
            <LoadingIndicator
              variant="dots"
              size="medium"
              color="primary"
              message="AI is thinking..."
            />
          </div>
        )}

        {/* Game Over Overlay */}
        {renderGameOverOverlay()}

        {/* ARIA Live Region */}
        <div
          className="cf-board__announcer"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {announcement}
        </div>
      </div>
    </div>
  );
};

export default ConnectFourBoard;
